import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import {
  useCreateStaffMutation,
  useGetGymsQuery,
  useGetStaffQuery,
} from "../../services";
import { RootState } from "../../redux/store";
import { useAppSelector } from "../../redux/hooks";
import { StaffFormData, StaffFormErrors } from "../../types";
import { adminRoutes } from "../../routers/AdminRouters";

const StaffCreate: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [createStaff, { isLoading: isCreating }] = useCreateStaffMutation();

  const {
    data: gymsData,
    isLoading: isGymsLoading,
    isError: isGymsError,
  } = useGetGymsQuery();

  const [formData, setFormData] = useState<StaffFormData>({
    name: "",
    email: "",
    phone: 0, // number type to match StaffFormData
    assignedGymId: "",
    assignedGyms: [], // Add array for multiple gym selection
    roleType: "",
    designation: "",
    status: "Active",
  });

  // keep a separate string for the input UI
  const [phoneInput, setPhoneInput] = useState<string>("");

  const [errors, setErrors] = useState<StaffFormErrors>({});

  const [isAssignedGymDropdownOpen, setIsAssignedGymDropdownOpen] =
    useState(false);

  const roles = ["Coach", "Trainer"];

  const emailRegex = /^\S+@\S+\.\S+$/;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "");
      setPhoneInput(cleaned);
      setFormData((prev) => ({
        ...prev,
        phone: cleaned ? Number(cleaned) : 0,
      }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value } as any));
    if ((errors as any)[name])
      setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  

  // New handler for multiple gym selection
  const handleAssignedGymChange = (gymId: string, isChecked: boolean) => {
    setFormData((prev) => {
      const updatedAssignedGyms = isChecked
        ? [...(prev.assignedGyms || []), gymId]
        : (prev.assignedGyms || []).filter((id) => id !== gymId);

      return {
        ...prev,
        assignedGyms: updatedAssignedGyms,
      };
    });
  };

  const validateForm = (): boolean => {
    const newErrors: StaffFormErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email.trim()))
      newErrors.email = "Email is invalid";
    if (!phoneInput.trim()) newErrors.phone = "Phone is required";
    if (!formData.assignedGymId) newErrors.assignedGymId = "Gym is required";
    if (!formData.roleType) newErrors.roleType = "Role type is required";
    if (!formData.designation.trim())
      newErrors.designation = "Designation is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await createStaff({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.roleType,
        designation: formData.designation,
        status: formData.status,
        gym: formData.assignedGymId,
        assignedGyms: formData.assignedGyms,
      } as any).unwrap();

      navigate("/staff");
    } catch (err) {
      console.error("Create staff failed:", err);
    }
  };

  // Sidebar compensation (unchanged)
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // Flatten gym options
  const gymOptions: Array<{ id: string; name: string }> = (gymsData ?? []).map(
    (g: any) => ({
      id: String(g.gymid ?? g._id ?? g.id ?? ""),
      name: g.name ?? g.gymName ?? "Unnamed Gym",
    })
  );

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${
        theme === "dark" ? "bg-black" : "bg-gray-50"
      } p-6`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`text-2xl font-semibold ${
            theme === "dark" ? "text-gray-300" : "text-gray-800"
          }`}
        >
          Create Staff
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
                         to={`${adminRoutes.staffList}`}
           
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Staff
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            Create New Staff
          </span>
        </div>
      </div>

      {/* Single form card (keeps existing card styling) */}
      <div
        className={`${
          theme === "dark"
            ? "bg-[#1E1E1E] border border-gray-800"
            : "bg-white border border-gray-200"
        } rounded-lg p-6`}
      >
        <div className="mb-6">
          <h2
            className={`text-base font-medium border-b pb-2 border-gray-700 ${
              theme === "dark" ? "text-white" : "text-gray-800"
            }`}
          >
            Create new Staff
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Row 1: Name, Email, Phone */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                }`}
              >
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter here"
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  errors.name
                    ? "border border-red-500"
                    : theme === "dark"
                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                }`}
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter here"
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  errors.email
                    ? "border border-red-500"
                    : theme === "dark"
                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                }`}
              >
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={phoneInput}
                onChange={handleChange}
                placeholder="Enter here"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={15}
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  errors.phone
                    ? "border border-red-500"
                    : theme === "dark"
                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>
          </div>

          {/* Row 2: Gym, Role Type, Designation */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                }`}
              >
                Gym
              </label>
              <select
                name="assignedGymId"
                value={formData.assignedGymId}
                onChange={handleChange}
                disabled={isGymsLoading || isGymsError}
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  errors.assignedGymId
                    ? "border border-red-500"
                    : theme === "dark"
                    ? "bg-black border border-gray-800 text-white"
                    : "bg-white border border-gray-300 text-gray-900"
                } focus:outline-none focus:ring-1 focus:ring-lime-500 appearance-none bg-no-repeat bg-right pr-8`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23ADADAD' d='M5 6L0 0h10z'/%3E%3C/svg%3E")`,
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "10px 6px",
                }}
              >
                <option value="">
                  {isGymsLoading
                    ? "Loading..."
                    : isGymsError
                    ? "Failed to load"
                    : "Choose from list"}
                </option>
                {gymOptions.map((gym) => (
                  <option key={gym.id} value={gym.id}>
                    {gym.name}
                  </option>
                ))}
              </select>
              {errors.assignedGymId && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.assignedGymId}
                </p>
              )}
            </div>
            <div className="relative">
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                }`}
              >
                Assigned Gyms
              </label>
              <div
                className={`w-full px-3 py-2.5 rounded-md text-sm border cursor-pointer ${
                  theme === "dark"
                    ? "bg-black border-gray-800 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                onClick={() =>
                  setIsAssignedGymDropdownOpen(!isAssignedGymDropdownOpen)
                }
              >
                <div className="flex items-center justify-between">
                  <span className="flex-1">
                    {formData.assignedGyms && formData.assignedGyms.length > 0
                      ? `${formData.assignedGyms.length} gym(s) selected`
                      : "Choose from list"}
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      isAssignedGymDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="#ADADAD"
                    viewBox="0 0 10 6"
                  >
                    <path d="M5 6L0 0h10z" />
                  </svg>
                </div>
              </div>

              {/* Selected gyms display */}
              {formData.assignedGyms && formData.assignedGyms.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {formData.assignedGyms.map((gymId) => {
                    const gym = gymOptions.find((g) => g.id === gymId);
                    return gym ? (
                      <span
                        key={gymId}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                          theme === "dark"
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {gym.name}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssignedGymChange(gymId, false);
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {/* Dropdown options */}
              {isAssignedGymDropdownOpen && (
                <div
                  className={`absolute z-10 mt-1 w-full rounded-md border shadow-lg ${
                    theme === "dark"
                      ? "bg-black border-gray-800"
                      : "bg-white border-gray-300"
                  }`}
                >
                  <div className="max-h-48 overflow-y-auto p-2">
                    {isGymsLoading ? (
                      <div
                        className={`p-2 text-sm ${
                          theme === "dark" ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Loading gyms...
                      </div>
                    ) : isGymsError ? (
                      <div className="p-2 text-sm text-red-500">
                        Failed to load gyms
                      </div>
                    ) : gymOptions.length === 0 ? (
                      <div
                        className={`p-2 text-sm ${
                          theme === "dark" ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        No gyms available
                      </div>
                    ) : (
                      gymOptions.map((gym) => (
                        <label
                          key={gym.id}
                          className={`flex items-center gap-2 p-2 hover:${
                            theme === "dark" ? "bg-gray-800" : "bg-gray-50"
                          } cursor-pointer rounded`}
                        >
                          <input
                            type="checkbox"
                            checked={(formData.assignedGyms || []).includes(
                              gym.id
                            )}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleAssignedGymChange(gym.id, e.target.checked);
                            }}
                            className="w-4 h-4 text-[#B6CB24] border-gray-300 rounded focus:ring-[#B6CB24] focus:ring-2"
                          />
                          <span
                            className={`text-sm ${
                              theme === "dark"
                                ? "text-gray-300"
                                : "text-gray-700"
                            }`}
                          >
                            {gym.name}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Click outside to close dropdown */}
              {isAssignedGymDropdownOpen && (
                <div
                  className="fixed inset-0 z-5"
                  onClick={() => setIsAssignedGymDropdownOpen(false)}
                />
              )}
            </div>

            <div>
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                }`}
              >
                Role Type
              </label>
              <select
                name="roleType"
                value={formData.roleType}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  errors.roleType
                    ? "border border-red-500"
                    : theme === "dark"
                    ? "bg-black border border-gray-800 text-white"
                    : "bg-white border border-gray-300 text-gray-900"
                } focus:outline-none focus:ring-1 focus:ring-lime-500 appearance-none bg-no-repeat bg-right pr-8`}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23ADADAD' d='M5 6L0 0h10z'/%3E%3C/svg%3E")`,
                  backgroundPosition: "right 0.75rem center",
                  backgroundSize: "10px 6px",
                }}
              >
                <option value="">Choose from list</option>
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              {errors.roleType && (
                <p className="text-red-500 text-xs mt-1">{errors.roleType}</p>
              )}
            </div>

            <div>
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700"
                }`}
              >
                Designation
              </label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                placeholder="Enter here"
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  errors.designation
                    ? "border border-red-500"
                    : theme === "dark"
                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
              {errors.designation && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.designation}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isCreating}
              className="px-8 py-1.5 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
            
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
            >
              {isCreating ? "Saving..." : "Save"}
            </button>
            <Link
              to={`${adminRoutes.staffList}`}
              className={`px-8 py-1.5 rounded-md font-medium ${
                theme === "dark"
                  ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StaffCreate;
